import { NetsuiteCreditMemoSources } from "@redotech/redo-model/integrations/returns/netsuite/netsuite";
import { CompensationMethod } from "@redotech/redo-model/return";
import {
  NetSuiteItemValueTypeZodSchema,
  NetSuiteTransactionTypeZodSchema,
} from "@redotech/redo-model/return-integration-types";
import {
  array,
  boolean,
  object,
  string,
  enum as zEnum,
  null as zNull,
  type infer as zInfer,
} from "zod";
import {
  netsuiteCashRefundCustomizationSchemaWithValidation,
  netsuiteCreditMemoCustomizationSchemaWithValidation,
  netsuiteProcessingCustomFieldSchema,
  netsuiteRMACustomizationSchemaWithValidation,
} from "./shared-schemas";

const returnTypeEnum = zEnum([
  "return",
  "claim",
  "warranty",
  "recycle",
  "withdrawal",
]);

export const upsertNetSuiteIntegration = {
  name: "upsertNetSuiteIntegration" as const,
  input: object({
    accountId: string().optional(),
    clientId: string().optional(),
    clientSecret: string().optional(),
    token: string().optional(),
    tokenSecret: string().optional(),
    transactionType: NetSuiteTransactionTypeZodSchema.optional(),
    shopifyOrderNameField: string().optional(),
    skuField: string().optional(),
    itemValue: NetSuiteItemValueTypeZodSchema.optional(),
    discountInventoryItemId: string().optional(),
    salesTaxItemId: string().optional(),
    itemReceiptSkuField: string().optional(),
    RMACustomization: netsuiteRMACustomizationSchemaWithValidation.optional(),
    itemReceipt: object({
      createItemReceipt: boolean().optional(),
      processReturnsEnabled: boolean().optional(),
      customFormId: string().optional(),
      processingCustomFields: array(
        netsuiteProcessingCustomFieldSchema,
      ).optional(),
    }).optional(),
    cashRefund: object({
      createCashRefund: boolean().optional(),
      cashRefundCustomizations:
        netsuiteCashRefundCustomizationSchemaWithValidation.optional(),
    }).optional(),
    creditMemo: object({
      createCreditMemo: boolean().optional(),
      customFormId: string().optional(),
      sources: array(zEnum(NetsuiteCreditMemoSources)).optional(),
      creditMemoCustomizations:
        netsuiteCreditMemoCustomizationSchemaWithValidation.optional(),
    }).optional(),
    customerRefund: object({
      createCustomerRefund: boolean().optional(),
      accountId: string().optional(),
      customFormId: string().optional(),
      orderIdField: string().optional(),
      allowedCompensationMethods: array(zEnum(CompensationMethod)).optional(),
    }).optional(),
    returnAuthorization: object({
      createReturnAuthorization: boolean().optional(),
      closeReturnAuthorization: object({
        enabled: boolean().optional(),
        returnTypes: array(returnTypeEnum).optional(),
        permissionList: array(zEnum(["close", "automation"])).optional(),
      }).optional(),
      allowFullGreenReturns: boolean().optional(),
      allowInStoreReturns: boolean().optional(),
      includeGreenItems: boolean().optional(),
      autoApproveReturnAuthorization: boolean().optional(),
      trackingNumberRequired: boolean().optional(),
      shipmentSettings: object({
        shouldUpdateShipment: boolean().optional(),
        shipmentStatusField: string().optional(),
        shipmentDateField: string().optional(),
        shipmentTrackerNumberField: string().optional(),
      }).optional(),
    }).optional(),
    vendorReturnAuthorization: object({
      enabled: boolean().optional(),
      closeVendorReturnAuthorization: object({
        enabled: boolean().optional(),
        returnTypes: array(returnTypeEnum).optional(),
      }).optional(),
    }).optional(),
  }),
  output: zNull(),
};

export type UpsertNetSuiteIntegrationInput = zInfer<
  typeof upsertNetSuiteIntegration.input
>;
